import { pickStringByPaths } from "./path";
import { DeepReportDocumentV1, normalizeReportDocumentV1 } from "../features/reports/reportDocumentV1";

// 深掘りレポート(P2)の型定義。既存のcompanyResearch.tsと同じくlib配下にフラットに置く。
// document rendererなど画面描画寄りの型はF2でfeatures/reports配下へ分離する想定
// (research-report-redesign-screen-spec.md「frontend配置案」参照)。

export type ReportRequestedType = "business" | "general";

export type ReportPerspectiveOption = { value: string; label: string };

// screen-specのUIモックにある観点chip(市場動向/影響/リスク/機会)を、
// report-redesign-api.mdのPOST /runs例にある値(industry_trend/self_impact)へ対応させた
// 暫定セット。Backend/AgentはPerspectivesを自由文字列として受け取るため厳密なenumではない。
export const REPORT_PERSPECTIVE_OPTIONS: ReportPerspectiveOption[] = [
  { value: "industry_trend", label: "市場動向" },
  { value: "self_impact", label: "影響" },
  { value: "risk", label: "リスク" },
  { value: "opportunity", label: "機会" },
];

export const REPORT_MAX_SELECTED_PERSPECTIVES = 4;
export const REPORT_MAX_SELECTED_ANGLES = 4;
export const REPORT_THEME_MAX_LENGTH = 200;
export const REPORT_PURPOSE_MAX_LENGTH = 1000;

export type ResearchAngle = {
  id: string;
  heading: string;
  why: string;
  queries: string[];
  selected: boolean;
};

export type ResearchPlan = {
  interpretation: string;
  reportType: ReportRequestedType;
  expectedModules: string[];
  angles: ResearchAngle[];
  warnings: string[];
};

export type CreateReportRunInput = {
  planRequestKey: string;
  theme: string;
  purpose?: string;
  requestedReportType?: ReportRequestedType | null;
  perspectives?: string[];
  regenerationMode?: "latest";
};

export type ReuseCandidate = {
  runId: string;
  theme: string;
  summary: string | null;
  informationCutoffAt: string | null;
  generatedAt: string | null;
};

export type ReuseCandidateAction = "open_previous" | "regenerate_latest" | "change_perspective";

export type CreateReportRunResult =
  | ({ reuseCandidate: undefined } & ReportPlanState)
  | { reuseCandidate: ReuseCandidate; actions: ReuseCandidateAction[] };

export type UpdateReportPlanAngleInput = {
  id: string;
  heading: string;
  why: string;
  selected: boolean;
  order: number;
};

export type UpdateReportPlanInput = {
  basePlanRevision: number;
  reportType: ReportRequestedType;
  angles: UpdateReportPlanAngleInput[];
};

export type StartReportRunInput = {
  planRevision: number;
  idempotencyKey: string;
};

export type StartReportRunResult = {
  runId: string;
  status: string;
  stage: string;
  usageCharged: boolean;
};

export type ReportAngleProgress = { id: string; heading: string; status: "pending" | "running" | "completed" | "failed"; evidenceCount: number };
export type ReportProgressState = { angles: ReportAngleProgress[]; elapsedSeconds: number };

/**
 * POST /runs、PATCH /runs/{runId}/plan、GET /runs/{runId} はそれぞれ応答形状が異なるが
 * (POST/PATCHはフラット、GETはrunオブジェクトの下)、F1が実際に必要とする
 * runId/status/planRevision/planはどちらの応答からも同じ形へ正規化して扱う。
 */
export type ReportPlanState = {
  runId: string;
  status: string;
  planRevision: number;
  plan: ResearchPlan | null;
  theme?: string;
  stage?: string;
  progress?: ReportProgressState | null;
  // schema_version不一致でdocumentのnormalize結果がnullになっても、plainTextさえ
  // あれば劣化表示できるようdocumentをnullable化している(currentDocument自体の有無とは分ける)。
  currentDocument?: { documentId: string; revision: number; document: DeepReportDocumentV1 | null; plainText: string } | null;
  warnings?: string[];
  error?: { code?: string | null; message?: string | null; retryable?: boolean } | null;
};

export type ReportRunListItem = { knowledgeItemId: string | null; runId: string; theme: string; reportType: string; status: string; stage: string; createdAt: string | null; updatedAt: string | null; summary: string | null };
export type ReportRunList = { items: ReportRunListItem[]; nextCursor: string | null };
export type ReportRunDeleteResult = { runId: string; deleted: boolean };

/**
 * DELETE /runs/{runId}でBackend(DeleteReportRunAction::DELETE_BLOCKED_STATUSES)が409にする
 * status。画面側の削除menu表示可否をAPI呼び出し前に判定するために同じ集合をここへ複製する。
 */
export const REPORT_RUN_DELETE_BLOCKED_STATUSES: readonly string[] = ["queued", "running"];

export type ReportClient = {
  createRun(input: CreateReportRunInput): Promise<CreateReportRunResult>;
  updatePlan(runId: string, input: UpdateReportPlanInput): Promise<ReportPlanState>;
  getRun(runId: string): Promise<ReportPlanState>;
  startRun(runId: string, input: StartReportRunInput): Promise<StartReportRunResult>;
  listRuns(params?: { cursor?: string; limit?: number; status?: string; reportType?: string }): Promise<ReportRunList>;
  deleteRun(runId: string): Promise<ReportRunDeleteResult>;
};

function normalizeStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

function normalizeProgress(raw: unknown): ReportProgressState | null {
  if (!raw || typeof raw !== "object") return null;
  const source = raw as Record<string, unknown>;
  const angles = Array.isArray(source.angles) ? source.angles.flatMap((item) => {
    if (!item || typeof item !== "object") return [];
    const angle = item as Record<string, unknown>;
    const status = angle.status;
    if (typeof angle.id !== "string" || typeof angle.heading !== "string" || !["pending", "running", "completed", "failed"].includes(String(status))) return [];
    return [{ id: angle.id, heading: angle.heading, status: status as ReportAngleProgress["status"], evidenceCount: typeof angle.evidence_count === "number" && angle.evidence_count >= 0 ? angle.evidence_count : 0 }];
  }) : [];
  return { angles, elapsedSeconds: typeof source.elapsed_seconds === "number" && source.elapsed_seconds >= 0 ? source.elapsed_seconds : 0 };
}

function normalizeResearchAngle(raw: unknown): ResearchAngle {
  const source = (raw ?? {}) as Record<string, unknown>;
  return {
    id: typeof source.id === "string" ? source.id : "",
    heading: typeof source.heading === "string" ? source.heading : "",
    why: typeof source.why === "string" ? source.why : "",
    queries: normalizeStringArray(source.queries),
    selected: source.selected === true,
  };
}

export function normalizeResearchPlan(raw: unknown): ResearchPlan | null {
  if (!raw || typeof raw !== "object") return null;
  const source = raw as Record<string, unknown>;
  // plan_jsonはAgent応答(snake_case)をほぼそのまま保存しているため、
  // report-redesign-api.mdのドキュメント例(camelCase)とcase不一致がある。
  // 両対応で読む(Backend/doc側の統一は別途検討)。
  const reportType = pickStringByPaths(source, ["reportType", "report_type"]);
  const angles = Array.isArray(source.angles) ? source.angles.map(normalizeResearchAngle) : [];

  return {
    interpretation: pickStringByPaths(source, ["interpretation"]) ?? "",
    reportType: reportType === "general" ? "general" : "business",
    expectedModules: normalizeStringArray(source.expectedModules ?? source.expected_modules),
    angles,
    warnings: normalizeStringArray(source.warnings),
  };
}

/** POST /runs・PATCH /runs/{runId}/plan の応答(フラット形状)を正規化する。 */
export function normalizeReportPlanResult(raw: unknown): ReportPlanState {
  const source = (raw ?? {}) as Record<string, unknown>;
  return {
    runId: typeof source.runId === "string" ? source.runId : "",
    status: typeof source.status === "string" ? source.status : "",
    planRevision: typeof source.planRevision === "number" ? source.planRevision : 0,
    plan: normalizeResearchPlan(source.plan),
  };
}

/** GET /runs/{runId} の応答(runオブジェクトの下に本体がある形状)を正規化する。 */
export function normalizeReportRunDetail(raw: unknown): ReportPlanState {
  const source = (raw ?? {}) as Record<string, unknown>;
  const run = (source.run ?? {}) as Record<string, unknown>;
  const current = source.currentDocument && typeof source.currentDocument === "object" ? source.currentDocument as Record<string, unknown> : null;
  const document = current ? normalizeReportDocumentV1(current.document) : null;
  const plainText = current && typeof current.plainText === "string" ? current.plainText : "";
  const error = source.error && typeof source.error === "object" ? source.error as Record<string, unknown> : null;
  return {
    runId: typeof run.runId === "string" ? run.runId : "",
    status: typeof run.status === "string" ? run.status : "",
    planRevision: typeof run.planRevision === "number" ? run.planRevision : 0,
    plan: normalizeResearchPlan(source.plan),
    theme: typeof run.theme === "string" ? run.theme : undefined,
    stage: typeof run.stage === "string" ? run.stage : undefined,
    progress: normalizeProgress(source.progress),
    // documentがnull(schema_version不一致)でもplainTextが残っていれば劣化表示に使うため、
    // currentDocument自体はdocument||plainTextのどちらかがあれば保持する。
    currentDocument: current && (document || plainText) ? { documentId: String(current.documentId ?? ""), revision: typeof current.revision === "number" ? current.revision : 0, document, plainText } : null,
    warnings: normalizeStringArray(source.warnings),
    error: error ? { code: typeof error.code === "string" ? error.code : null, message: typeof error.message === "string" ? error.message : null, retryable: error.retryable === true } : null,
  };
}

export function normalizeReportRunList(raw: unknown): ReportRunList {
  const source = (raw ?? {}) as Record<string, unknown>;
  const items = Array.isArray(source.items) ? source.items : [];
  return { items: items.filter((item): item is Record<string, unknown> => !!item && typeof item === "object").map((item) => ({ knowledgeItemId: typeof item.knowledgeItemId === "string" && item.knowledgeItemId.trim() ? item.knowledgeItemId : null, runId: String(item.runId ?? ""), theme: String(item.theme ?? ""), reportType: String(item.reportType ?? ""), status: String(item.status ?? ""), stage: String(item.stage ?? ""), createdAt: typeof item.createdAt === "string" ? item.createdAt : null, updatedAt: typeof item.updatedAt === "string" ? item.updatedAt : null, summary: typeof item.summary === "string" ? item.summary : null })), nextCursor: typeof source.nextCursor === "string" ? source.nextCursor : null };
}

export function normalizeReportRunDeleteResult(raw: unknown): ReportRunDeleteResult {
  const source = (raw ?? {}) as Record<string, unknown>;
  return { runId: typeof source.runId === "string" ? source.runId : "", deleted: source.deleted === true };
}

export function normalizeReuseCandidate(raw: unknown): ReuseCandidate {
  const source = (raw ?? {}) as Record<string, unknown>;
  return {
    runId: typeof source.runId === "string" ? source.runId : "",
    theme: typeof source.theme === "string" ? source.theme : "",
    summary: typeof source.summary === "string" ? source.summary : null,
    informationCutoffAt: typeof source.informationCutoffAt === "string" ? source.informationCutoffAt : null,
    generatedAt: typeof source.generatedAt === "string" ? source.generatedAt : null,
  };
}

export function normalizeCreateReportRunResult(raw: unknown): CreateReportRunResult {
  const source = (raw ?? {}) as Record<string, unknown>;
  if (source.reuseCandidate && typeof source.reuseCandidate === "object") {
    const actions = Array.isArray(source.actions)
      ? source.actions.filter(
          (a): a is ReuseCandidateAction =>
            a === "open_previous" || a === "regenerate_latest" || a === "change_perspective"
        )
      : [];
    return {
      reuseCandidate: normalizeReuseCandidate(source.reuseCandidate),
      actions,
    };
  }

  return { reuseCandidate: undefined, ...normalizeReportPlanResult(source) };
}

/**
 * planRequestKey/idempotencyKeyはBackendが`uuid`形式で厳密検証するため、
 * device.tsのcreateUuid相当のRFC4122 v4フォールバックを持つ生成関数をここでも用意する。
 */
export function createReportRequestUuid(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (typeof c?.randomUUID === "function") return c.randomUUID();

  if (typeof c?.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    c.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    let hex = "";
    for (let i = 0; i < bytes.length; i++) hex += bytes[i].toString(16).padStart(2, "0");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  let hex = "";
  for (let i = 0; i < 16; i++) hex += Math.floor(Math.random() * 256).toString(16).padStart(2, "0");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-4${hex.slice(13, 16)}-8${hex.slice(17, 20)}-${hex.slice(20)}`;
}

export function normalizeStartReportRunResult(raw: unknown): StartReportRunResult {
  const source = (raw ?? {}) as Record<string, unknown>;
  return {
    runId: typeof source.runId === "string" ? source.runId : "",
    status: typeof source.status === "string" ? source.status : "",
    stage: typeof source.stage === "string" ? source.stage : "",
    usageCharged: source.usageCharged === true,
  };
}
